import {
  Box,
  FieldSwitch,
  FlexBox,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import * as React from 'react';
import { Field } from 'redux-form';

import { FieldRadio } from 'components/settings/components/sections/dixaMessenger/views/appearance/components/generalSettings/languageSettings/components/languageSelector/components/languageSelectorItem/components/FieldRadio';
import { MessengerTranslatedLanguage } from 'components/settings/components/sections/dixaMessenger/views/appearance/components/generalSettings/languageSettings/languages';
import { messengerSettingsFormFields } from 'components/settings/components/sections/dixaMessenger/views/appearance/types';

import style from './style.module.scss';

interface LanguageSelectorItemProps {
  langCode: MessengerTranslatedLanguage;
  defaultLanguageCode: MessengerTranslatedLanguage;
  langName: string;
  isEnabled: boolean;
  thereIsOnlyOneEnabledLang: boolean;
  isCurrentlySelected: boolean;
  onSwitchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick: React.MouseEventHandler;
}

const canNotDisableTheLangIfItIsTheOnlyEnabledOneTooltipContent =
  'You can’t disable all languages. At least one language has to be selected.';
const canNotDisableTheDefaultLanguageTooltipContent =
  'You can’t disable a language that is chosen as default. Activate more languages if you want to change the default.';

const preventBubblingOfClickEvents: React.MouseEventHandler<
  HTMLOrSVGElement
> = (e) => e.stopPropagation();

export function LanguageSelectorItem({
  langCode,
  defaultLanguageCode,
  langName,
  isEnabled,
  thereIsOnlyOneEnabledLang,
  isCurrentlySelected,
  onSwitchChange,
  onClick,
}: LanguageSelectorItemProps): JSX.Element {
  const className = classNames(style.root, {
    [style.selected]: isCurrentlySelected,
  });

  const disableDefaultLangSelector = thereIsOnlyOneEnabledLang || !isEnabled;
  const thisLangIsTheDefaultLanguage = langCode === defaultLanguageCode;
  const thisLangIsTheOnlyEnabledLang = thereIsOnlyOneEnabledLang && isEnabled;
  const isSwitchDisabled =
    thisLangIsTheOnlyEnabledLang || thisLangIsTheDefaultLanguage;

  const tooltipContent = thisLangIsTheOnlyEnabledLang
    ? canNotDisableTheLangIfItIsTheOnlyEnabledOneTooltipContent
    : thisLangIsTheDefaultLanguage
    ? canNotDisableTheDefaultLanguageTooltipContent
    : undefined;

  return (
    <button className={className} type="button" onClick={onClick}>
      <FlexBox alignItems="center" gap="300">
        <Tooltip content={tooltipContent}>
          <div onClick={preventBubblingOfClickEvents}>
            <FieldSwitch
              className={classNames({
                [style.clickNotAllowed]: isSwitchDisabled,
              })}
              disabled={isSwitchDisabled}
              input={{
                name: 'availableLanguage',
                checked: isEnabled,
                value: langCode,
                onChange: onSwitchChange,
              }}
            />
          </div>
        </Tooltip>

        <Text.Paragraph>{langName}</Text.Paragraph>
      </FlexBox>

      <Box
        onClick={preventBubblingOfClickEvents}
        className={classNames(style.defaultLanguageRadioInput, {
          [style.clickNotAllowed]: disableDefaultLangSelector,
        })}
      >
        <Field
          type="radio"
          id={langCode}
          value={langCode}
          component={FieldRadio}
          disabled={disableDefaultLangSelector}
          name={messengerSettingsFormFields.defaultLanguage}
        />
      </Box>
    </button>
  );
}
